import { supabase } from '@/lib/supabase';
import type {
  ExtractEnvelope,
  ExtractLabOutput,
  ExtractPrescriptionOutput,
  ExtractScanOutput,
} from '@/types/db';

export type ExtractResult =
  | { success: true; envelope: ExtractEnvelope }
  | { success: false; error: ExtractErrorCode };

export type ExtractErrorCode =
  | 'extraction_failed'
  | 'extraction_timeout'
  | 'storage_unreadable'
  | 'unauthorized'
  | 'forbidden'
  | 'invalid_input'
  | 'network_error'
  // 2-call pipeline additions (Day-3)
  | 'unsupported_doc_type'
  | 'low_confidence_classifier'
  | 'schema_invalid'
  | 'classifier_failed';

export async function extractPrescription(input: {
  storagePath: string;
  profileId: string;
}): Promise<ExtractResult> {
  try {
    const { data, error } = await supabase.functions.invoke(
      'extract-prescription',
      {
        body: {
          storage_path: input.storagePath,
          profile_id: input.profileId,
        },
      },
    );
    if (error) {
      const ctx = (error as { context?: Response }).context;
      let status: number | undefined;
      let bodyText = '';
      let fnVersion: string | null = null;
      if (ctx && typeof ctx.text === 'function') {
        status = ctx.status;
        fnVersion = ctx.headers.get('x-caremory-fn-version');
        try {
          bodyText = await ctx.clone().text();
        } catch {
          /* ignore body read failure */
        }
      }
      console.error('[extract] function invoke error', { status, fnVersion, body: bodyText, error });
      let bodyCode: ExtractErrorCode | undefined;
      if (bodyText) {
        try {
          const parsed = JSON.parse(bodyText) as { error?: ExtractErrorCode };
          if (parsed && typeof parsed.error === 'string') bodyCode = parsed.error;
        } catch {
          /* non-JSON body */
        }
      }
      if (bodyCode) return { success: false, error: bodyCode };
      if (status === 401) return { success: false, error: 'unauthorized' };
      if (status === 403) return { success: false, error: 'forbidden' };
      if (status === 400) return { success: false, error: 'invalid_input' };
      if (typeof status === 'number' && status >= 500) {
        return { success: false, error: 'extraction_failed' };
      }
      return { success: false, error: 'network_error' };
    }
    if (!data || typeof data !== 'object') {
      return { success: false, error: 'extraction_failed' };
    }
    if (data.success === true && data.data) {
      const docType = data.doc_type;
      if (docType === 'prescription') {
        return {
          success: true,
          envelope: {
            doc_type: 'prescription',
            data: data.data as ExtractPrescriptionOutput,
          },
        };
      }
      if (docType === 'lab_report') {
        return {
          success: true,
          envelope: {
            doc_type: 'lab_report',
            data: data.data as ExtractLabOutput,
          },
        };
      }
      if (docType === 'scan') {
        return {
          success: true,
          envelope: {
            doc_type: 'scan',
            data: data.data as ExtractScanOutput,
          },
        };
      }
      // Unknown doc_type — treat as unsupported rather than misclassifying.
      console.error('[extract] unknown doc_type', docType);
      return { success: false, error: 'unsupported_doc_type' };
    }
    if (data.diag) {
      console.error('[extract] diag', data.diag);
    }
    const code = (data.error as ExtractErrorCode) ?? 'extraction_failed';
    return { success: false, error: code };
  } catch (err) {
    console.error('[extract] unexpected', err);
    return { success: false, error: 'network_error' };
  }
}

export function extractErrorMessage(code: ExtractErrorCode): string {
  switch (code) {
    case 'extraction_timeout':
      return "Reading the document took too long. You can type the details instead.";
    case 'storage_unreadable':
      return "We couldn't open the image. Try taking the photo again.";
    case 'forbidden':
      return "You don't have access to this profile.";
    case 'unauthorized':
      return 'Please sign in again.';
    case 'invalid_input':
      return 'Something about this photo confused us. Try again.';
    case 'network_error':
      return "We couldn't reach the server. Check your connection.";
    case 'unsupported_doc_type':
      return "We couldn't tell what kind of document this is. Try a clearer photo, or type the details.";
    case 'low_confidence_classifier':
      return "We weren't confident about this document type. Try a clearer photo.";
    case 'schema_invalid':
      return "We read the document but couldn't structure the result. You can type the details instead.";
    case 'classifier_failed':
      return "We couldn't read this document. You can type the details instead.";
    default:
      return "We couldn't read this document. You can type the details instead.";
  }
}
